import {AxiosInstance} from "axios";
import {
    MedicationCheckup,
    MedicationCheckupCycleState,
    MedicationCheckupRunReport
} from "@/medication-checkup/interfaces";
import {streamServerSentEvents} from "@/core/http/sse";

/**
 * Stream one narrated run of a block, as Server-Sent Events.
 *
 * Every run of the course speaks the vocabulary of the card that plays it:
 * `step` opens a stage, `line` and `failed` land on the stage open, `done`
 * closes the run, `error` refuses it in-band. Every word comes served, so
 * the reports go to the scene as they are. A stream ending without `done` —
 * refused, or cut — rejects, and the scene falls back on the plain
 * submission.
 *
 * Progress reporting only: the cycle state is re-read afterwards, and that
 * read is what the scenes render from.
 */
const streamRun = async (
    hdsClient: AxiosInstance,
    url: string,
    onReport: (report: MedicationCheckupRunReport) => void,
    signal?: AbortSignal
): Promise<void> => {
    let done = false;
    let refused: string | null = null;

    await streamServerSentEvents(
        hdsClient,
        url,
        ({event, data}) => {
            const payload = (data ?? {}) as {title?: string; lines?: number | null; text?: string; message?: string};

            switch (event) {
                case 'step':
                    onReport({type: 'step', title: payload.title ?? '', lines: payload.lines ?? null});

                    return;

                case 'line':
                case 'failed':
                    onReport({type: event, text: payload.text ?? ''});

                    return;

                case 'done':
                    done = true;

                    return;

                case 'error':
                    refused = payload.message ?? `The run of ${url} was refused`;

                    return false;
            }
        },
        signal
    );

    if (!done) {
        throw new Error(refused ?? `The run of ${url} closed without a done event`);
    }
};

/**
 * Resource for the HDS medication checkup endpoints (module « BPM Copilote »).
 *
 * The BPM is served by the HDS alone, so this resource takes the hds client
 * only: reaching for the api one would put health data on the shared base.
 */
const MedicationCheckupResource = (hdsClient: AxiosInstance) => {
    return {

        /**
         * Get the medication review of a patient in the current pharmacy.
         *
         * No review yet answers 204 with an empty body — a legitimate state,
         * not an error.
         */
        async readForCustomer(customerId: number): Promise<MedicationCheckup | null> {
            const response = await hdsClient.get(`customers/${customerId}/medication-checkup`);

            return response.data?.data ?? null;
        },

        /**
         * Enter the medication review of a patient, opening what is missing.
         *
         * The single way into the course, called on every entry: the endpoint
         * is idempotent, and answers the whole cycle state — resume point
         * included, views composed for the step the course stands at — so
         * entering costs one round trip rather than a creation followed by a
         * read.
         */
        async enter(customerId: number): Promise<MedicationCheckupCycleState> {
            const response = await hdsClient.post(`customers/${customerId}/medication-checkup`);

            return response.data.data;
        },

        /**
         * Delete a medication review, whole.
         *
         * The review is the unit that is dropped, not the cycle in hand: the
         * row goes with every cycle it holds, the ones already closed
         * included. Addressed by its own id, which the cycle state carries —
         * the customer keys the review only where it is read or entered.
         */
        async deleteCheckup(checkupId: number): Promise<void> {
            await hdsClient.delete(`medication-checkups/${checkupId}`);
        },

        /**
         * Get the state of a cycle: the pinned definition, the blocks and the
         * resume point, in one call. The thread is not served — the front
         * renders it scene by scene from this.
         *
         * The views are composed for one step only: the one named, else the
         * one the course stands at. Every other block is served bare.
         */
        async readCycle(
            cycleId: number,
            stepKey: string | null = null
        ): Promise<MedicationCheckupCycleState> {
            const response = await hdsClient.get(`medication-checkup-cycles/${cycleId}`, {
                params: stepKey === null ? undefined : {step: stepKey}
            });

            return response.data.data;
        },

        /**
         * Submit a block, advancing the course.
         *
         * Answers the whole cycle rather than the block alone, since a
         * submission moves the resume point: one round trip, and no second
         * read to see the scene that follows. The views come back composed
         * for the step of the submitted block.
         */
        async submitBlock(
            blockId: number,
            payload: Record<string, unknown> = {}
        ): Promise<MedicationCheckupCycleState> {
            const response = await hdsClient.post(
                `medication-checkup-blocks/${blockId}`,
                {payload}
            );

            return response.data.data;
        },

        /**
         * Download a document of a cycle, at the url the handler served.
         *
         * The url is absolute, so the client applies its interceptors — the
         * token first — and not its base url. The HDS renders the PDF on
         * request and streams it: the blob is the whole file.
         */
        async downloadDocument(url: string): Promise<Blob> {
            const response = await hdsClient.get(url, {responseType: 'blob'});

            return response.data;
        },

        /**
         * Scan the record of a block, narrated: the dispensings collected,
         * then the prescriptions read.
         */
        async scanBlock(
            blockId: number,
            onReport: (report: MedicationCheckupRunReport) => void,
            signal?: AbortSignal
        ): Promise<void> {
            return streamRun(hdsClient, `medication-checkup-blocks/${blockId}/stream`, onReport, signal);
        },

        /**
         * Run the analysis of a block, narrated source by source.
         */
        async analyzeBlock(
            blockId: number,
            onReport: (report: MedicationCheckupRunReport) => void,
            signal?: AbortSignal
        ): Promise<void> {
            return streamRun(hdsClient, `medication-checkup-blocks/${blockId}/stream`, onReport, signal);
        },

        /**
         * Ask the AI draft of a block, narrated as the AI makes it.
         */
        async draftBlock(
            blockId: number,
            onReport: (report: MedicationCheckupRunReport) => void,
            signal?: AbortSignal
        ): Promise<void> {
            return streamRun(hdsClient, `medication-checkup-blocks/${blockId}/stream`, onReport, signal);
        },
    };
};

export default MedicationCheckupResource;
